import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  RefreshCw,
  Users,
  Clock,
  MousePointer,
  FileText,
  TrendingUp,
  LogOut,
  LayoutDashboard,
  BarChart3,
  Calculator,
  Globe,
  Ship,
} from "lucide-react";
import { FaFacebook, FaInstagram, FaTiktok, FaWhatsapp } from "react-icons/fa";
import axios from "axios";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOCIAL_COLORS = {
  facebook: "#1877F2",
  instagram: "#E4405F",
  tiktok: "#000000",
  whatsapp: "#25D366",
};

const SOCIAL_ICONS = {
  facebook: FaFacebook,
  instagram: FaInstagram,
  tiktok: FaTiktok,
  whatsapp: FaWhatsapp,
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7d");

  const fetchStats = async () => {
    setLoading(true);
    const token = localStorage.getItem("adminToken");
    
    try {
      const response = await axios.get(`${API}/analytics/stats?period=${period}&token=${token}`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/espace-pro-fastandcar");
      }
      toast.error("Erreur de chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Set page title and noindex
    document.title = "Statistiques - Espace Pro | Fast&Car";
    
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    
    fetchStats();
    
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, [period]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/espace-pro-fastandcar");
    toast.success("Déconnexion réussie");
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // Prepare pie chart data for social clicks
  const socialPieData = stats ? Object.entries(stats.social_by_network)
    .filter(([_, value]) => value > 0)
    .map(([network, value]) => ({
      name: network.charAt(0).toUpperCase() + network.slice(1),
      value,
      color: SOCIAL_COLORS[network],
    })) : [];

  return (
    <div data-testid="analytics-page" className="pt-32 md:pt-40 min-h-screen bg-zinc-950">
      {/* Header */}
      <section className="py-3 md:py-4 bg-black border-b border-zinc-800 sticky top-16 md:top-24 z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <h1 className="font-unbounded text-base md:text-xl font-bold text-white">
                <BarChart3 className="w-5 h-5 inline mr-2 text-neon-lime" />
                Statistiques <span className="text-neon-lime">du site</span>
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white w-40 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="7d">7 derniers jours</SelectItem>
                  <SelectItem value="30d">30 derniers jours</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                onClick={fetchStats}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400 h-9"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
              
              <Button
                onClick={() => navigate("/espace-pro-fastandcar/dashboard")}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400 h-9"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">CRM</span>
              </Button>
              
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="border-red-800 text-red-500 h-9"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {loading ? (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-zinc-900 border border-zinc-800 p-6 animate-pulse">
                <div className="h-4 bg-zinc-800 w-1/2 mb-4" />
                <div className="h-8 bg-zinc-800 w-3/4" />
              </div>
            ))}
          </div>
        </div>
      ) : stats ? (
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {/* Visitors Today */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-neon-lime" />
                <span className="text-zinc-500 text-sm">Aujourd'hui</span>
              </div>
              <p className="font-unbounded text-2xl md:text-3xl font-bold text-white">
                {stats.summary.visitors_today}
              </p>
              <p className="text-zinc-500 text-xs mt-1">visiteurs</p>
            </div>
            
            {/* Visitors This Week */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                <span className="text-zinc-500 text-sm">Cette semaine</span>
              </div>
              <p className="font-unbounded text-2xl md:text-3xl font-bold text-white">
                {stats.summary.visitors_this_week}
              </p>
              <p className="text-zinc-500 text-xs mt-1">visiteurs uniques</p>
            </div>
            
            {/* Avg Session Duration */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                <span className="text-zinc-500 text-sm">Durée moyenne</span>
              </div>
              <p className="font-unbounded text-2xl md:text-3xl font-bold text-white">
                {stats.summary.avg_session_duration_formatted}
              </p>
              <p className="text-zinc-500 text-xs mt-1">par session</p>
            </div>
            
            {/* Social Clicks */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <MousePointer className="w-5 h-5 text-pink-500" />
                <span className="text-zinc-500 text-sm">Clics réseaux</span>
              </div>
              <p className="font-unbounded text-2xl md:text-3xl font-bold text-white">
                {stats.summary.total_social_clicks}
              </p>
              <p className="text-zinc-500 text-xs mt-1">total sur {period === "7d" ? "7j" : "30j"}</p>
            </div>
          </div>

          {/* Simulations Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {/* Total Simulations */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <Calculator className="w-5 h-5 text-orange-500" />
                <span className="text-zinc-500 text-sm">Simulations</span>
              </div>
              <p className="font-unbounded text-2xl md:text-3xl font-bold text-white">
                {stats.summary.total_simulations || 0}
              </p>
              <p className="text-zinc-500 text-xs mt-1">total sur {period === "7d" ? "7j" : "30j"}</p>
            </div>
            
            {/* Europe Simulations */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <Globe className="w-5 h-5 text-blue-500" />
                <span className="text-zinc-500 text-sm">Import Europe</span>
              </div>
              <p className="font-unbounded text-2xl md:text-3xl font-bold text-blue-500">
                {stats.simulations_by_type?.europe || 0}
              </p>
              <p className="text-zinc-500 text-xs mt-1">simulations</p>
            </div>
            
            {/* Korea Simulations */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <div className="flex items-center gap-3 mb-2">
                <Ship className="w-5 h-5 text-purple-500" />
                <span className="text-zinc-500 text-sm">Import Corée</span>
              </div>
              <p className="font-unbounded text-2xl md:text-3xl font-bold text-purple-500">
                {stats.simulations_by_type?.korea || 0}
              </p>
              <p className="text-zinc-500 text-xs mt-1">simulations</p>
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Visitors Chart */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <h3 className="font-unbounded text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-neon-lime" />
                Visiteurs par jour
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={stats.visitors_chart}>
                  <defs>
                    <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#39FF14" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#39FF14" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#18181b", border: "1px solid #27272a", color: "#fff" }}
                    labelStyle={{ color: "#a1a1aa" }}
                  />
                  <Area
                    type="monotone"
                    dataKey="visitors"
                    stroke="#39FF14"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorVisitors)"
                    name="Visiteurs"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Social Clicks by Network */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <h3 className="font-unbounded text-lg font-bold text-white mb-4 flex items-center gap-2">
                <MousePointer className="w-5 h-5 text-pink-500" />
                Clics par réseau social
              </h3>
              {stats.summary.total_social_clicks > 0 ? (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={socialPieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {socialPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: "#18181b", border: "1px solid #27272a", color: "#fff" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Legend with icons */}
                  <div className="flex flex-wrap md:flex-col gap-3 justify-center">
                    {Object.entries(stats.social_by_network).map(([network, count]) => {
                      const Icon = SOCIAL_ICONS[network];
                      return (
                        <div key={network} className="flex items-center gap-2">
                          <Icon className="w-5 h-5" style={{ color: SOCIAL_COLORS[network] }} />
                          <span className="text-white text-sm capitalize">{network}</span>
                          <span className="text-zinc-500 text-sm">({count})</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-zinc-500">
                  Aucun clic enregistré sur cette période
                </div>
              )}
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Social Clicks Over Time */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <h3 className="font-unbounded text-lg font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Évolution des clics réseaux
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.social_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#18181b", border: "1px solid #27272a", color: "#fff" }}
                  />
                  <Bar dataKey="facebook" stackId="a" fill={SOCIAL_COLORS.facebook} name="Facebook" />
                  <Bar dataKey="instagram" stackId="a" fill={SOCIAL_COLORS.instagram} name="Instagram" />
                  <Bar dataKey="tiktok" stackId="a" fill="#666666" name="TikTok" />
                  <Bar dataKey="whatsapp" stackId="a" fill={SOCIAL_COLORS.whatsapp} name="WhatsApp" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Top Pages */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <h3 className="font-unbounded text-lg font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Pages les plus visitées
              </h3>
              {stats.top_pages.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_pages.map((page, index) => {
                    const maxViews = stats.top_pages[0]?.views || 1;
                    const percentage = (page.views / maxViews) * 100;
                    return (
                      <div key={index} className="relative">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-sm truncate max-w-[200px]">
                            {page.page === "/" ? "Accueil" : page.page.replace(/^\//, "")}
                          </span>
                          <span className="text-neon-lime font-bold text-sm">{page.views}</span>
                        </div>
                        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-neon-lime to-green-600 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-zinc-500">
                  Aucune donnée disponible
                </div>
              )}
            </div>
          </div>

          {/* Simulations Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Simulations Over Time */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <h3 className="font-unbounded text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Calculator className="w-5 h-5 text-orange-500" />
                Simulations par jour
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.simulations_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#18181b", border: "1px solid #27272a", color: "#fff" }}
                  />
                  <Bar dataKey="europe" fill="#3B82F6" name="🇪🇺 Europe" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="korea" fill="#A855F7" name="🇰🇷 Corée" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded" />
                  <span className="text-zinc-400 text-sm">Europe</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-purple-500 rounded" />
                  <span className="text-zinc-400 text-sm">Corée</span>
                </div>
              </div>
            </div>

            {/* Simulations Pie Chart */}
            <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
              <h3 className="font-unbounded text-lg font-bold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-orange-500" />
                Répartition des simulations
              </h3>
              {(stats.summary.total_simulations || 0) > 0 ? (
                <div className="flex flex-col md:flex-row items-center gap-6">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Europe", value: stats.simulations_by_type?.europe || 0, color: "#3B82F6" },
                          { name: "Corée", value: stats.simulations_by_type?.korea || 0, color: "#A855F7" },
                        ].filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        <Cell fill="#3B82F6" />
                        <Cell fill="#A855F7" />
                      </Pie>
                      <Tooltip
                        contentStyle={{ backgroundColor: "#18181b", border: "1px solid #27272a", color: "#fff" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                      <Globe className="w-6 h-6 text-blue-500" />
                      <div>
                        <p className="text-white font-bold">{stats.simulations_by_type?.europe || 0}</p>
                        <p className="text-zinc-500 text-xs">Import Europe</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Ship className="w-6 h-6 text-purple-500" />
                      <div>
                        <p className="text-white font-bold">{stats.simulations_by_type?.korea || 0}</p>
                        <p className="text-zinc-500 text-xs">Import Corée</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-zinc-500">
                  Aucune simulation sur cette période
                </div>
              )}
            </div>
          </div>

          {/* Additional Stats */}
          <div className="bg-zinc-900 border border-zinc-800 p-4 md:p-6">
            <h3 className="font-unbounded text-lg font-bold text-white mb-4">
              Résumé de la période ({period === "7d" ? "7 jours" : "30 jours"})
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center p-4 bg-zinc-800/50 rounded">
                <p className="text-3xl font-bold text-neon-lime">{stats.summary.total_pageviews}</p>
                <p className="text-zinc-500 text-sm">Pages vues</p>
              </div>
              <div className="text-center p-4 bg-zinc-800/50 rounded">
                <p className="text-3xl font-bold text-blue-500">{stats.summary.total_unique_visitors}</p>
                <p className="text-zinc-500 text-sm">Visiteurs uniques</p>
              </div>
              <div className="text-center p-4 bg-zinc-800/50 rounded">
                <p className="text-3xl font-bold text-yellow-500">{stats.summary.total_sessions}</p>
                <p className="text-zinc-500 text-sm">Sessions</p>
              </div>
              <div className="text-center p-4 bg-zinc-800/50 rounded">
                <p className="text-3xl font-bold text-pink-500">{stats.summary.total_social_clicks}</p>
                <p className="text-zinc-500 text-sm">Clics réseaux</p>
              </div>
              <div className="text-center p-4 bg-zinc-800/50 rounded">
                <p className="text-3xl font-bold text-orange-500">{stats.summary.total_simulations || 0}</p>
                <p className="text-zinc-500 text-sm">Simulations</p>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AnalyticsPage;
